import { Component, computed, inject, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { IAdvizeComponent } from '../../../admin/components/iAdvize/iAdvize.component';
import { AppConfig } from '../../../AppConfig';
import { SignLegalDocumentModalComponent } from '../../../legal-documents/components/sign-legal-document-modal/sign-legal-document-modal.component';
import { useLegalDocumentSigning } from '../../../legal-documents/composables/use-legal-document-signing';
import { NotificationPopoverContainer } from '../../../notifications/components/notification-popover/notification-popover.container';
import { NavTab, NavTabComponent } from '../../../shared/components/nav-tab/nav-tab.component';
import { SomfyproLogoComponent } from '../../../shared/components/somfypro-logo/somfypro-logo.component';
import { ToasterHostComponent } from '../../../shared/components/toaster/toaster-host/toaster-host.component';
import { APP_CONFIG } from '../../../shared/injectionTokens';
import { User } from '../../../shared/models/User';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { UISocketService } from '../../../shared/services/ui.socket.service';
import { AppState } from '../../../shared/store/app-state';
import { getUserLoginState } from '../../../shared/store/selectors/login';
import { getUserContactPage, getUserEnvLoadingState } from '../../../shared/store/selectors/user-env';
import { getSomfyProUrl } from '../../../utils/url';
import { SideNavComponent } from '../side-nav/side-nav.component';

const DASHBOARD_ROUTES = ['/sites', '/alerts', '/link-requests'];

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        SomfyproLogoComponent,
        NavTabComponent,
        NotificationPopoverContainer,
        SideNavComponent,
        RouterOutlet,
        RouterLink,
        ToasterHostComponent,
        IAdvizeComponent,
        SignLegalDocumentModalComponent,
    ],
})
export class HeaderComponent implements OnInit, OnDestroy {
    private readonly uiSocketService = inject(UISocketService);
    private readonly router = inject(Router);
    private readonly store = inject<Store<AppState>>(Store);
    private readonly analyticService = inject(AnalyticsService);
    private readonly appConfig = inject<AppConfig>(APP_CONFIG);
    private readonly translate = inject(TranslateService);
    protected readonly legalDocumentSigning = useLegalDocumentSigning();

    private unsubscribe$: Subject<void> = new Subject();

    readonly userEnvLoading$: Observable<boolean>;

    readonly userLogin$: Observable<User | null>;
    userLogin: User | undefined;

    somfyProUrl: string | undefined;
    private readonly contactPageUrl = this.store.selectSignal(getUserContactPage);

    private readonly currentUrl = signal(this.router.url);
    private readonly langChange = toSignal(this.translate.onLangChange);

    private readonly activeTab = computed<'dashboard' | 'notifications' | null>(() => {
        const url = this.currentUrl();
        if (url.startsWith('/notifications')) return 'notifications';
        if (DASHBOARD_ROUTES.some((route) => url.startsWith(route))) return 'dashboard';
        return null;
    });

    protected readonly notificationsActive = computed(() => this.activeTab() === 'notifications');

    protected readonly tabs = computed<NavTab[]>(() => {
        this.langChange();
        const contactPageUrl = this.contactPageUrl();

        const tabs: NavTab[] = [
            {
                label: this.translate.instant('DASHBOARD'),
                routerLink: '/sites',
                active: this.activeTab() === 'dashboard',
            },
        ];

        if (contactPageUrl) {
            tabs.push({
                label: this.translate.instant('SUPPORT'),
                href: contactPageUrl,
                onClick: () => this.analyticService.accessGlobalMenuDocument('assistance'),
            });
        }

        return tabs;
    });

    protected trackNotificationsClicked(): void {
        this.analyticService.clickMessagesMenu();
    }

    constructor() {
        this.userEnvLoading$ = this.store.select(getUserEnvLoadingState);
        this.userLogin$ = this.store.select(getUserLoginState);

        this.uiSocketService.initSocket();
    }

    ngOnInit(): void {
        this.userLogin$.pipe(takeUntil(this.unsubscribe$)).subscribe((user: User | null) => {
            if (!user) return;
            this.userLogin = user;
            this.somfyProUrl = getSomfyProUrl(this.userLogin.country, this.appConfig.name);
        });
        this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.currentUrl.set(event.urlAfterRedirects);
            }
        });
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
